import { GetServerSidePropsContext } from "next";
import { useTranslation } from "next-i18next";

import { BreakoutWidth } from "@/components/BreakoutWidth";
import { PageLayout } from "@/components/layouts";
import { PaddingWrapper } from "@/components/PaddingWrapper";
import { ProductCard } from "@/components/ProductCard";
import styles from "@/components/ProductCategories/styles.module.css";
import SelectByLocale from "@/components/SelectByLocale";
import SubcategoriesGrid from "@/components/SubcategoriesGrid";
import SubcategoriesPage from "@/components/SubcategoriesPage";
import { getSingleCategoryData } from "@/services/productService/productService";
import { getSiteSettings } from "@/services/sanity";
import { I18nextLanguage, I18nextLocale } from "@/types/types";
import { getAllTranslationsServerSide } from "@/utils/i18n";
import { getLocaleParts } from "@/utils/localisation-utils";
export type ProductCategoryPageProductProps = {
  id: number;
  title: string;
  brand: {
    name: string;
  };
  img: string;
  imgHover?: string;
  relativePackaging: number;
  size_original: number;
  size: number;
  price: {
    formatted: string;
    price: number;
    currency: string;
    unitPrice: string;
    litrePrice: string;
  };
  inventory: number;
  slug: string;
  skuCode: string;
  lang: string;
  country: string;
  category: {
    key: string;
    slug: string;
  };
};

type ProductCategoryPageProps = {
  products?: ProductCategoryPageProductProps[];
  title: string;
  blurb: string;
  metaTitle: string;
  metaDescription: string;
};

export default function ProductCategoryPage({
  products = [],
  title,
  blurb,
  metaTitle,
  metaDescription,
}: ProductCategoryPageProps) {
  const { t } = useTranslation();
  const productsCopy = [...products];

  const sortedProducts = productsCopy?.sort(
    (prodA, prodB) =>
      prodA.brand.name
        .toLowerCase()
        .localeCompare(prodB.brand.name.toLowerCase()) ||
      prodA.title.toLowerCase().localeCompare(prodB.title.toLowerCase())
  );

  return (
    <PageLayout
      title={metaTitle}
      description={metaDescription}
      headerTheme="dark"
    >
      <SubcategoriesPage title={title} blurb={blurb}>
        <SubcategoriesGrid>
          {sortedProducts?.map((product) => (
            <ProductCard product={product} key={product.skuCode} />
          ))}
        </SubcategoriesGrid>
      </SubcategoriesPage>
      <BreakoutWidth as="section" className={styles.products}>
        <PaddingWrapper onlyX>
          <SubcategoriesPage
            subTitle={t("other_products")}
            displayBanner={false}
          >
            <SubcategoriesGrid>
              <SelectByLocale visibleIfLanguage={I18nextLanguage.NO}>
                <SubcategoriesGrid.Card
                  as="li"
                  imgSrc="/assets/product-images/soap.jpg"
                  slug="/nettbutikk/bestill-handsape-refill"
                  title="Såpe"
                />
                <SubcategoriesGrid.Card
                  as="li"
                  imgSrc="/assets/product-images/cleaning.jpeg"
                  slug="/nettbutikk/bestill-rengjoringsmidler-refill"
                  title="Rengjøring"
                />
                <SubcategoriesGrid.Card
                  as="li"
                  imgSrc="/assets/product-images/laundry.jpeg"
                  slug="/nettbutikk/bestill-vaskemiddel-refill"
                  title="Vaskemiddel"
                />
              </SelectByLocale>
              <SelectByLocale visibleIfLanguage={I18nextLanguage.EN}>
                <SubcategoriesGrid.Card
                  as="li"
                  imgSrc="/assets/product-images/soap.jpg"
                  slug="/nettbutikk/bestill-handsape-refill"
                  title="Soap"
                />
                <SubcategoriesGrid.Card
                  as="li"
                  imgSrc="/assets/product-images/cleaning.jpeg"
                  slug="/nettbutikk/bestill-rengjoringsmidler-refill"
                  title="Cleaning"
                />
                <SubcategoriesGrid.Card
                  as="li"
                  imgSrc="/assets/product-images/laundry.jpeg"
                  slug="/nettbutikk/bestill-vaskemiddel-refill"
                  title="Laundry detergent"
                />
              </SelectByLocale>
            </SubcategoriesGrid>
          </SubcategoriesPage>
        </PaddingWrapper>
      </BreakoutWidth>
    </PageLayout>
  );
}

export async function getServerSideProps({
  locale,
  params,
}: GetServerSidePropsContext) {
  const { lang } = getLocaleParts(locale);

  try {
    const pageData = await getSingleCategoryData(
      params.categorySlug as string,
      lang as string
    );

    const { category, products } = pageData;
    const { title, description, metaTitle, metaDescription } = category;

    const siteSettings = await getSiteSettings();

    return {
      props: {
        ...(await getAllTranslationsServerSide(locale as I18nextLocale)),
        title,
        blurb: description,
        products: products,
        metaTitle,
        metaDescription,
        siteSettings,
      },
    };
  } catch (e) {
    console.error("Error fetching category data:", e);
    return {
      notFound: true,
    };
  }
}
